import { dashboardApi } from '@/store/api/base/api.base';
import { dropEndpoint, writeEndpoint } from '@/store/api/begateway/begateway.endpoints.cache';
import type { TypeAppDispatch } from '@/store/store';
import type {
    TypeCatalogState,
    TypeEndpointFilterState,
    TypeEndpointLimitedFilter,
    TypeGatewayEndpoint,
    TypeGatewayEndpointDetail,
    TypeRouteCatalog,
} from '@/types/begateway/types.begateway.endpoint';
import type { TypeRouteLimit, TypeRuleInput } from '@/types/begateway/types.begateway.limit';

type TypeEndpointCreateInput = { method: string; path: string; limit?: TypeRuleInput | null };

type TypeRouteEndpoints = { route: string; serviceId: string | null; catalog: TypeRouteCatalog | null; limit: TypeRouteLimit; endpoints: TypeGatewayEndpoint[] };

type TypeServiceEndpoints = { endpoints: TypeGatewayEndpoint[]; catalogs: Record<string, TypeRouteCatalog>; truncated: boolean };

type TypeCatalogSync = {
    report: { route: string; state: TypeCatalogState; changed: boolean; added: number; reactivated: number; removed: number; active: number; error: string | null };
    catalog: TypeRouteCatalog | null;
};

type TypeEndpointQuery = { state?: TypeEndpointFilterState; method?: string; q?: string; limited?: TypeEndpointLimitedFilter };

type TypeRouteRef = { kind: string; name: string };

export const SERVICE_ENDPOINTS_TAG = 'SERVICE';

export const routeEndpointsArgs = (route: string): TypeRouteRef & { state: 'all' } => {
    const [kind = '', name = ''] = route.split('/');
    return { kind, name, state: 'all' };
};

export const serviceEndpointsArgs = (serviceId: string): { serviceId: string; state: 'all' } => ({ serviceId, state: 'all' });

const clean = (query: TypeEndpointQuery): Record<string, string> => Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
) as Record<string, string>;

const target = ({ kind, name }: TypeRouteRef): string => `/begateway/routes/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`;

const endpointUrl = (id: string): string => `/begateway/endpoints/${encodeURIComponent(id)}`;

const writeFromMutation = async (dispatch: TypeAppDispatch, queryFulfilled: Promise<{ data: TypeGatewayEndpointDetail }>) => {
    try {
        const { data } = await queryFulfilled;
        writeEndpoint(dispatch, data);
    } catch {
    }
};

export const begatewayEndpointsApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        readRouteEndpoints: build.query<TypeRouteEndpoints, TypeRouteRef & TypeEndpointQuery>({
            query: ({ kind, name, ...query }) => ({ url: `${target({ kind, name })}/endpoints`, params: clean(query) }),
            providesTags: (_result, _error, { kind, name }) => [{ type: 'GatewayEndpoint', id: `${kind}/${name}` }],
        }),

        readServiceEndpoints: build.query<TypeServiceEndpoints, { serviceId: string } & TypeEndpointQuery>({
            query: ({ serviceId, ...query }) => ({ url: `/begateway/services/${encodeURIComponent(serviceId)}/endpoints`, params: clean(query) }),
            providesTags: (result) => [
                { type: 'GatewayEndpoint', id: SERVICE_ENDPOINTS_TAG },
                ...Object.keys(result?.catalogs ?? {}).map((id) => ({ type: 'GatewayEndpoint' as const, id })),
            ],
        }),

        readGatewayEndpoint: build.query<TypeGatewayEndpointDetail, string>({
            query: (id) => ({ url: endpointUrl(id) }),
            providesTags: (_result, _error, id) => [{ type: 'GatewayEndpoint', id }],
        }),

        syncRouteEndpoints: build.mutation<TypeCatalogSync, TypeRouteRef>({
            query: (ref) => ({ url: `${target(ref)}/endpoints/sync`, method: 'post' }),
            invalidatesTags: (_result, _error, { kind, name }) => [
                { type: 'GatewayEndpoint', id: `${kind}/${name}` },
                { type: 'GatewayEndpoint', id: SERVICE_ENDPOINTS_TAG },
            ],
        }),

        updateEndpointLimit: build.mutation<TypeGatewayEndpointDetail, { id: string; limit: TypeRuleInput }>({
            query: ({ id, limit }) => ({ url: `${endpointUrl(id)}/limit`, method: 'patch', data: { limit } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => writeFromMutation(dispatch, queryFulfilled),
        }),

        removeEndpointLimit: build.mutation<TypeGatewayEndpointDetail, { id: string }>({
            query: ({ id }) => ({ url: `${endpointUrl(id)}/limit`, method: 'delete' }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => writeFromMutation(dispatch, queryFulfilled),
        }),

        createGatewayEndpoint: build.mutation<TypeGatewayEndpointDetail, TypeRouteRef & TypeEndpointCreateInput>({
            query: ({ kind, name, ...body }) => ({ url: `${target({ kind, name })}/endpoints`, method: 'post', data: body }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => writeFromMutation(dispatch, queryFulfilled),
        }),

        removeGatewayEndpoint: build.mutation<{ id: string; route: string }, { id: string; route: string; serviceId: string | null }>({
            query: ({ id }) => ({ url: endpointUrl(id), method: 'delete' }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropEndpoint(dispatch, arg);
                    dispatch(begatewayEndpointsApi.util.invalidateTags(['GatewayAccess']));
                } catch {
                }
            },
        }),
    }),
});

export const {
    useReadRouteEndpointsQuery,
    useReadServiceEndpointsQuery,
    useReadGatewayEndpointQuery,
    useSyncRouteEndpointsMutation,
    useUpdateEndpointLimitMutation,
    useRemoveEndpointLimitMutation,
    useCreateGatewayEndpointMutation,
    useRemoveGatewayEndpointMutation,
} = begatewayEndpointsApi;
